import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { BaseService } from '../services/base.service';
import { getCssVariable, getRGBColor } from '../utils/utils';

@Injectable({
    providedIn: 'root',
})
export class ThemeService extends BaseService {
    private renderer2: Renderer2;

    constructor(
        rendererFactory: RendererFactory2,
        private meta: Meta
    ) {
        super();
        this.renderer2 = rendererFactory.createRenderer(null, null);
    }

    loadTheme(themeData: any | undefined) {
        function importCustomFont(
            headingFontUrl: string | undefined,
            headingFontFamily: string | undefined,
            bodyFontUrl: string | undefined,
            bodyFontFamily: string | undefined
        ) {
            let importStyles = '';
            let fontFaceStyles = '';

            if (headingFontUrl) {
                importStyles += `@import url('${headingFontUrl}');\n`;
                fontFaceStyles += `
                @font-face {
                    font-family: '${headingFontFamily}';
                    src: url('${headingFontUrl}') format('truetype');
                }
                `;
            }

            if (bodyFontUrl) {
                importStyles += `@import url('${bodyFontUrl}');\n`;
                fontFaceStyles += `
                @font-face {
                    font-family: '${bodyFontFamily}';
                    src: url('${bodyFontUrl}') format('truetype');
                }
                `;
            }

            //  <style> for @import (they must be at the top)
            if (importStyles.trim().length > 0) {
                const importNode = document.createElement('style');
                importNode.innerHTML = importStyles;
                document.head.appendChild(importNode);
            }

            // <style> for @font-face
            if (fontFaceStyles.trim().length > 0) {
                const fontFaceNode = document.createElement('style');
                fontFaceNode.innerHTML = fontFaceStyles;
                document.head.appendChild(fontFaceNode);
            }
        }

        // Default fonts (used if not overridden by config)
        const DEFAULT_HEADING_FONT = 'Montserrat';
        const DEFAULT_BODY_FONT = 'Inter var';

        let headingFont = themeData?.fonts?.heading?.url ?? '';
        let bodyFont = themeData?.fonts?.body?.url ?? '';
        let headingFontFamily = themeData?.fonts?.heading?.family || DEFAULT_HEADING_FONT;
        let bodyFontFamily = themeData?.fonts?.body?.family || DEFAULT_BODY_FONT;
        let primary = themeData?.colors?.primary ?? '#0f172a';
        let primaryContrast = themeData?.colors?.primaryContrast ?? '#ffffff';
        let appNav = themeData?.colors?.appNav ?? '#0f172a';
        let appNavContrast = themeData?.colors?.appNavContrast ?? '#ffffff';
        let appHeading = themeData?.colors?.appHeading ?? '#ac2000';
        let appHeadingContrast = themeData?.colors?.appHeadingContrast ?? '#ffffff';

        //colors
        const primaryColor = getRGBColor(primary, 'primary');
        const primaryContrastColor = getRGBColor(primaryContrast, 'primary-contrast');
        const appNavColor = getRGBColor(appNav, 'app-nav');
        const appNavContrastColor = getRGBColor(appNavContrast, 'app-nav-contrast');
        const appHeadingColor = getRGBColor(appHeading, 'app-heading');
        const appHeadingContrastColor = getRGBColor(appHeadingContrast, 'app-heading-contrast');

        //swiper controls customization
        const swiperNavigationColor = `--swiper-navigation-color: ${primary};`;
        const swiperPaginationColor = `--swiper-pagination-color: ${primary};`;

        // 1. Import custom font from remote if customs
        if (headingFont || bodyFont) {
            importCustomFont(headingFont, headingFontFamily, bodyFont, bodyFontFamily);
        }

        const headingFontVariable = getCssVariable(`${headingFontFamily}, sans-serif`, 'heading-font');
        const bodyFontVariable = getCssVariable(`${bodyFontFamily}, sans-serif`, 'body-font');

        const cssVariables = `:root { ${headingFontVariable} ${bodyFontVariable} ${primaryColor} ${primaryContrastColor} ${appNavColor} ${appNavContrastColor} ${appHeadingColor} ${appHeadingContrastColor} ${swiperNavigationColor} ${swiperPaginationColor} }`;

        // 2. Always add font variables, if not specified we use the default ones
        const style = document.createElement('style');
        style.innerHTML = cssVariables;
        document.head.appendChild(style);

        // 3. Add CSS rules from config
        this.importCss(themeData?.cssContent);

        // 4. Update meta tag to match heading color
        this.meta.updateTag({ name: 'theme-color', content: appHeading });
    }

    private importCss(cssContent?: string) {
        if (!cssContent) return;

        // Create a style element via Angular renderer
        const style = this.renderer2.createElement('style') as HTMLStyleElement;

        // Add the style content
        style.innerHTML = cssContent;

        // Append the style to the head section
        this.renderer2.appendChild(document.head, style);
    }
}
